import { rootACL } from '../root/rootAcls';
import { ADD, ALL_WILDCARD, ASSIGN_PROGRAM, DELETE, EDIT, MANUAL_ADD, MENU, VIEW } from './ACL';

interface ACLBase {
  [key: string]: string | ACLBase;
}

interface NotesACLs extends ACLBase {
  ALL: string;
  VIEW: string;
  ADD: string;
}

interface CareTeamACLs extends ACLBase {
  ALL: string;
  VIEW: string;
  ADD: string;
  EDIT: string;
  MEMBERS: {
    ALL: string;
    ADD: string;
    EDIT: string;
    DELETE: string;
  };
}

interface TaskACLs extends ACLBase {
  ALL: string;
  VIEW: string;
  ADD: string;
  EDIT: string;
  HANDLER: {
    ALL: string;
    REFERRAL_INTERNAL: string;
    REFERRAL_EXTERNAL: string;
    WARM_HANDOFF: string;
    APPOINTMENT: string;
    ASSIGN_PROGRAM: string;
  };
  NOTES: NotesACLs;
  INPUT_OUTPUT: {
    ALL: string;
    EDIT: string;
    DELETE: string;
    VIEW: string;
  };
}

interface AttachmentsACLs extends ACLBase {
  ALL: string;
  VIEW: string;
  ADD: string;
  EDIT: string;
}

interface WorkflowACLs extends ACLBase {
  CLOSE: string;
  NEXT: string;
  VIEW: string;
  OWNER: string;
}

const getACLs = <T extends ACLBase>(acls: T) => acls;

const getFlatACLsList = (acls: ACLBase): string[] => {
  const keys = Object.keys(acls);

  return keys.reduce<string[]>((list, key) => {
    if (typeof acls[key] === 'object') return [...list, ...getFlatACLsList(acls[key] as ACLBase)];

    return [...list, acls[key] as string];
  }, []);
};

//Admin ACLs
const adminACL = rootACL.newChild('admin');
//Admin Users ACLs
const adminUsersACL = adminACL.newChild('users');
//Admin Org ACLs
const adminOrganizationsACL = adminACL.newChild('organizations');
const adminOrganizationsChildOrgsACL = adminOrganizationsACL.newChild('childOrganizations');
const adminOrganizationsOwnOrgACL = adminOrganizationsACL.newChild('ownOrganization');
const adminOrganizationsIdentifierDetails = adminOrganizationsACL.newChild('identifierDetails');
const adminOrganizationsUserOrgRole = adminOrganizationsACL.newChild('userOrgRole');
const adminOrganizationsIdentifierDetailsHidden =
  adminOrganizationsIdentifierDetails.newChild('hidden');

// Fhir Org
const adminFhirOrganizationsACL = adminACL.newChild('fhirOrganizations');
const adminFhirOrganizationsAddressDetails = adminFhirOrganizationsACL.newChild('addressDetails');
const adminFhirOrganizationsContactDetails = adminFhirOrganizationsACL.newChild('contactDetails');
const adminFhirOrganizationsContacts = adminFhirOrganizationsACL.newChild('contacts');
const adminFhirOrganizationsIdentifierDetails =
  adminFhirOrganizationsACL.newChild('identifierDetails');
const adminFhirOrganizationsChildOrgsACL = adminFhirOrganizationsACL.newChild('childOrganizations');
const adminFhirOrganizationsAffiliateOrgsACL =
  adminFhirOrganizationsACL.newChild('affiliateOrganizations');
const adminFhirOrganizationsOwnOrgACL = adminFhirOrganizationsACL.newChild('ownOrganization');
const adminFhirOrganizationsIdentifierDetailsHidden =
  adminOrganizationsIdentifierDetails.newChild('hidden');
const adminFhirOrganizationsTransferPatientACL =
  adminFhirOrganizationsACL.newChild('transferPatient');
const adminFhirOrganizationsIdentifierDetailsExternal =
  adminFhirOrganizationsACL.newChild('external');

//Admin Permissions ACLs
const adminPermissionsACL = adminACL.newChild('permissions');
const adminWorkflowACL = adminACL.newChild('workflow');
const adminChecklistACL = adminACL.newChild('checklist');

//Admin Custom Fields
const customFieldsACL = adminACL.newChild('customFields');

//CRS ACLs
const crsACL = rootACL.newChild('crs');

//CRS Dashboard
const crsDashboardACL = crsACL.newChild('dashboard');
const crsDashboardDashletACL = crsACL.newChild('dashlet');
const crsDashletMyOrganizationPatientsACL =
  crsDashboardDashletACL.newChild('myOrganizationPatients');
const crsDashletMyPatientEngagementsACL = crsDashboardDashletACL.newChild('myPatientEngagements');
const crsDashletOrphanTasksACL = crsDashboardDashletACL.newChild('orphanTasks');
const crsDashletOrphanGoalsACL = crsDashboardDashletACL.newChild('orphanGoals');

//CRS Patient ACLs
const crsPatientACL = crsACL.newChild('patient');
const crsPatientSearchACL = crsPatientACL.newChild('search');
const crsPatientPersonalDetailsACL = crsPatientACL.newChild('personalDetails');
const crsPatientManagingOrganizationACL = crsPatientACL.newChild('managingOrganization');
const crsPatientContactDetailsACL = crsPatientACL.newChild('contactDetails');
const crsPatientAddressDetailsACL = crsPatientACL.newChild('addressDetails');
const crsPatientIdentifierDetailsACL = crsPatientACL.newChild('identifierDetails');
const crsPatientIdentifierDetailsACLHidden = crsPatientIdentifierDetailsACL.newChild('hidden');
const crsPatientIdentifierDetailsACLExternal = crsPatientIdentifierDetailsACL.newChild('external');
const crsPatientAssessmentsACL = crsPatientACL.newChild('assessments');
const crsPatientInsuranceACL = crsPatientACL.newChild('insurance');
const crsPatientTabsACL = crsPatientACL.newChild('tabs');
const crsPatientCasesACL = crsPatientACL.newChild('cases');
const crsPatientCarePlanACL = crsPatientACL.newChild('carePlan');
const crsPatientServiceRequestACL = crsPatientACL.newChild('serviceRequest');
const crsPatientServiceRequestNotesACL = crsPatientServiceRequestACL.newChild('notes');
const crsPatientServiceRequestTasksACL = crsPatientServiceRequestACL.newChild('tasks');
const crsPatientServiceRequestTasksHandlerACL = crsPatientServiceRequestACL.newChild('handler');
const crsPatientServiceRequestTasksNotesACL = crsPatientServiceRequestTasksACL.newChild('notes');
const crsPatientServiceRequestTasksInputOutputACL =
  crsPatientServiceRequestTasksACL.newChild('inputOutput');
const crsPatientImmunizationACL = crsPatientACL.newChild('immunization');
const crsPatientObservationACL = crsPatientACL.newChild('observation');
const crsPatientAppointmentACL = crsPatientACL.newChild('appointment');
const crsPatientConditionACL = crsPatientACL.newChild('condition');
const crsPatientEpisodeOfCareACL = crsPatientACL.newChild('episodeOfCare');
const crsPatientDeviceACL = crsPatientACL.newChild('device');
const crsPatientGroupsACL = crsPatientACL.newChild('groups');
const crsPatientGroupsMembersACL = crsPatientGroupsACL.newChild('members');
const crsPatientContactsACL = crsPatientACL.newChild('contacts');
const crsPatientRelatedPersonsACL = crsPatientACL.newChild('relatedPersons');
const crsPatientCareTeamsACL = crsPatientACL.newChild('careTeams');
const crsPatientCareTeamsMembersACL = crsPatientCareTeamsACL.newChild('members');
const crsPatientAttachmentsACL = crsPatientACL.newChild('attachments');
const crsPatientNotesACL = crsPatientACL.newChild('notes');
const crsPatientTasksACL = crsPatientACL.newChild('tasks');
const crsPatientTasksHandlerACL = crsPatientACL.newChild('handler');
const crsPatientTasksNotesACL = crsPatientTasksACL.newChild('tasks');
const crsPatientTasksInputOutputACL = crsPatientTasksACL.newChild('inputOutput');
const crsPatientNATTaskACL = crsPatientACL.newChild('natTask');
const crsPatientConsentACL = crsPatientACL.newChild('consent');
const crsPatientCardACL = crsPatientACL.newChild('card');
const crsPatientSILACL = crsPatientACL.newChild('sil');
const crsPatientPRSACL = crsPatientACL.newChild('prs');
const crsPatientInckeligibleACL = crsPatientACL.newChild('inckeligible');
const crsPatientGeneralPractitionerACL = crsPatientACL.newChild('generalPractitioner');
const crsPatientDeactivateACL = crsPatientACL.newChild('deactivate');
const crsPatientLinkACL = crsPatientACL.newChild('link');
const crsPatientCustomFieldsDetailsACL = crsPatientACL.newChild('customFields');
const crsPatientBasicInformationACL = crsPatientACL.newChild('basicInformation');

//CRS Referral
const crsReferralACL = crsACL.newChild('referral');
const crsReferralIdentifierDetailsACL = crsReferralACL.newChild('identifierDetails');
const crsReferralIdentifierDetailsACLHidden = crsReferralIdentifierDetailsACL.newChild('hidden');
const crsReferralWorkflowACL = crsReferralACL.newChild('workflow');
const crsChildReferralACL = crsReferralACL.newChild('childReferral');
const crsReferralConsentACL = crsReferralACL.newChild('consent');
const crsReferralContactAttemptsACL = crsReferralACL.newChild('contactAttempts');
const crsReferralObservationACL = crsReferralACL.newChild('observation');
const crsReferralTaskACL = crsReferralACL.newChild('task');
const crsReferralTaskHandlerAcl = crsReferralTaskACL.newChild('handler');
const crsReferralTaskNotesACL = crsReferralTaskACL.newChild('notes');
const crsReferralTaskInputOutputACL = crsReferralTaskACL.newChild('inputOutput');

//CRS Case
const crsCaseACL = crsACL.newChild('case');
const crsCaseGoalACL = crsCaseACL.newChild('goal');
const crsCaseGoalNotesACL = crsCaseGoalACL.newChild('notes');
const crsCaseTaskACL = crsCaseACL.newChild('task');
const crsCaseTaskNotesACL = crsCaseTaskACL.newChild('notes');
const crsCaseTaskHandlerAcl = crsCaseTaskACL.newChild('handler');
const crsCaseTaskInputOutputACL = crsCaseTaskACL.newChild('inputOutput');
const crsCaseCareTeamsACL = crsCaseACL.newChild('careTeams');
const crsCaseCareTeamsMembersACL = crsCaseCareTeamsACL.newChild('members');
const crsCaseContactAttemptsACL = crsCaseACL.newChild('contactAttempts');
const crsCaseNotesACL = crsCaseACL.newChild('notes');
const crsCaseAttachmentsACL = crsCaseACL.newChild('attachments');
const crsCaseWorkflowACL = crsCaseACL.newChild('workflow');
const crsCaseStatisticsACL = crsCaseACL.newChild('statistics');
const crsCaseServiceRequestACL = crsCaseACL.newChild('serviceRequests');
const crsCaseServiceRequestTasksACL = crsCaseServiceRequestACL.newChild('task');
const crsCaseServiceRequestTasksHandlerACL = crsCaseServiceRequestACL.newChild('handler');
const crsCaseServiceRequestNotesACL = crsCaseServiceRequestACL.newChild('notes');
const crsCaseServiceRequestTasksNotesACL = crsCaseServiceRequestTasksACL.newChild('notes');
const crsCaseServiceRequestTasksInputOutputACL =
  crsCaseServiceRequestTasksACL.newChild('inputOutput');
const crsCaseSummaryACL = crsCaseACL.newChild('summary');
const crsCaseDetailsACL = crsCaseACL.newChild('details');
const crsCaseComplementaryInfoACL = crsCaseACL.newChild('complementaryInfo');
const crsRelatedReferralsACL = crsCaseACL.newChild('relatedReferrals');
const crsCaseOutboundReferralACL = crsCaseACL.newChild('outboundReferral');
const crsCaseAppointmentACL = crsCaseACL.newChild('appointment');
const crsCasePatientACL = crsCaseACL.newChild('patient');
const crsCaseAssessmentACL = crsCaseACL.newChild('assessment');
const crsCaseObservationACL = crsCaseACL.newChild('observation');
const crsCaseConditionACL = crsCaseACL.newChild('condition');
const crsCaseFamilyMembersACL = crsCaseACL.newChild('familyMembers');
const crsCaseRelatedPersonsACL = crsCaseACL.newChild('relatedPersons');
const crsCaseGroupsACL = crsCaseACL.newChild('groups');
const crsCaseGroupsMemberACL = crsCaseGroupsACL.newChild('members');

//CRS Task
const crsTaskACL = crsACL.newChild('task');
const crsTaskNotesACL = crsACL.newChild('notes');
//CRS Provider Window
const crsProviderWindowACL = crsACL.newChild('provider_window');
//CRS Data Quality
const dataQuality = rootACL.newChild('dataQuality');
const dataQualityIncidentACL = dataQuality.newChild('incident');
//CRS Practitioner
const crsPractitionerACL = crsACL.newChild('practitioner');
const crsPractitionerPersonalDetailsACL = crsPractitionerACL.newChild('personalDetails');
const crsPractitionerContactDetailsACL = crsPractitionerACL.newChild('contactDetails');
const crsPractitionerIdentifierDetailsACL = crsPractitionerACL.newChild('identifierDetails');
const crsPractitionerIdentifierDetailsACLHidden =
  crsPractitionerIdentifierDetailsACL.newChild('hidden');
const crsPractitionerIdentifierDetailsACLExternal =
  crsPractitionerIdentifierDetailsACL.newChild('external');
const crsPractitionerLanguageDetailsACL = crsPractitionerACL.newChild('languageDetails');
const crsPractitionerAddressDetailsACL = crsPractitionerACL.newChild('addressDetails');
const crsPractitionerRolesACL = crsPractitionerACL.newChild('roles');
//CRS Tags
const crsTagsACL = crsACL.newChild('tags');

//YoCare ACLs
const yoCareACL = rootACL.newChild('yoCare');
//YoCare Users ACLs
const yoCareUsersACL = yoCareACL.newChild('users');
//YoCare Persons ACLs
const yoCarePersonsACL = yoCareACL.newChild('persons');
//YoCare DataSystems ACLs
const yoCareDataSystemsACL = yoCareACL.newChild('dataSystems');

//Engagement HUB
const engagementHubACL = rootACL.newChild('engagementHub');
const engagementHubPatientEngagementACL = engagementHubACL.newChild('patientEngagement');
const engagementHubPatientEngagementNotesACL = engagementHubPatientEngagementACL.newChild('notes');
const engagementHubPatientEngagementAttachmentsACL =
  engagementHubPatientEngagementACL.newChild('attachments');
const engagementHubPatientEngagementWorkflowACL =
  engagementHubPatientEngagementACL.newChild('workflow');
const engagementHubPatientEngagementAppointmentACL =
  engagementHubPatientEngagementACL.newChild('appointment');
const engagementHubPatientEngagementTasksACL = engagementHubPatientEngagementACL.newChild('tasks');
const engagementHubPatientEngagementTasksNotesACL =
  engagementHubPatientEngagementTasksACL.newChild('notes');
const engagementHubPatientEngagementTasksHandlerACL =
  engagementHubPatientEngagementTasksACL.newChild('handler');
const engagementHubPatientEngagementStatisticsACL =
  engagementHubPatientEngagementACL.newChild('statistics');
//NAT
const natAcl = rootACL.newChild('NAT');
const natTaskAcl = natAcl.newChild('task');
const natTaskNotesACL = natTaskAcl.newChild('notes');
const natServiceRequestACL = natAcl.newChild('serviceRequest');

const crsAcls = {
  ALL: rootACL.concat(ALL_WILDCARD),
  ADMIN: {
    ALL: adminACL.concat(ALL_WILDCARD),
    USERS: {
      ALL: adminUsersACL.concat(ALL_WILDCARD),
      MENU: adminUsersACL.concat(MENU),
      VIEW: adminUsersACL.concat(VIEW),
      ADD: adminUsersACL.concat(ADD),
      EDIT: adminUsersACL.concat(EDIT),
      PROFILE: adminUsersACL.concat('profile'),
      CARDS: adminUsersACL.concat('cards'),
      ACCOUNT: adminUsersACL.concat('account'),
      ALL_ORGANIZATION: adminUsersACL.concat('allOrganization'),
      OWN_ORGANIZATION: adminUsersACL.concat('ownOrganization'),
      OWN_AND_CHILD_ORGANIZATION: adminUsersACL.concat('ownAndChilOrganization'),
    },
    ORGANIZATIONS: {
      ALL: adminOrganizationsACL.concat(ALL_WILDCARD),
      MENU: adminOrganizationsACL.concat(MENU),
      ADD: adminOrganizationsACL.concat(ADD),
      VIEW: adminOrganizationsACL.concat(VIEW),
      EDIT: adminOrganizationsACL.concat(EDIT),
      DELETE: adminOrganizationsACL.concat(DELETE),
      CHILD_ORGANIZATIONS: {
        ALL: adminOrganizationsChildOrgsACL.concat(ALL_WILDCARD),
        VIEW: adminOrganizationsChildOrgsACL.concat(VIEW),
        ADD: adminOrganizationsChildOrgsACL.concat(ADD),
      },
      OWN_ORGANIZATION: {
        ALL: adminOrganizationsOwnOrgACL.concat(ALL_WILDCARD),
        VIEW: adminOrganizationsOwnOrgACL.concat(VIEW),
      },
      IDENTIFIER_DETAILS: {
        ALL: adminOrganizationsIdentifierDetails.concat(ALL_WILDCARD),
        VIEW: adminOrganizationsIdentifierDetails.concat(VIEW),
        ADD: adminOrganizationsIdentifierDetails.concat(ADD),
        EDIT: adminOrganizationsIdentifierDetails.concat(EDIT),
        DELETE: adminOrganizationsIdentifierDetails.concat(DELETE),
        HIDDEN: {
          VIEW: adminOrganizationsIdentifierDetailsHidden.concat(VIEW),
          EDIT: adminOrganizationsIdentifierDetailsHidden.concat(EDIT),
          DELETE: adminOrganizationsIdentifierDetails.concat(DELETE),
        },
      },
      USER_ORG_ROLE: {
        ALL: adminOrganizationsUserOrgRole.concat(ALL_WILDCARD),
        ADD: adminOrganizationsUserOrgRole.concat(ADD),
        DELETE: adminOrganizationsUserOrgRole.concat(DELETE),
      },
    },
    FHIR_ORGANIZATIONS: {
      ALL: adminFhirOrganizationsACL.concat(ALL_WILDCARD),
      MENU: adminFhirOrganizationsACL.concat(MENU),
      ADD: adminFhirOrganizationsACL.concat(ADD),
      VIEW: adminFhirOrganizationsACL.concat(VIEW),
      EDIT: adminFhirOrganizationsACL.concat(EDIT),
      DELETE: adminFhirOrganizationsACL.concat(DELETE),
      ACTIVATE: adminFhirOrganizationsACL.concat('activate'),
      DEACTIVATE: adminFhirOrganizationsACL.concat('deactivate'),
      CHILD_ORGANIZATIONS: {
        ALL: adminFhirOrganizationsChildOrgsACL.concat(ALL_WILDCARD),
        VIEW: adminFhirOrganizationsChildOrgsACL.concat(VIEW),
        ADD: adminFhirOrganizationsChildOrgsACL.concat(ADD),
        EDIT: adminFhirOrganizationsChildOrgsACL.concat(EDIT),
      },
      AFFILIATE_ORGANIZATIONS: {
        ALL: adminFhirOrganizationsAffiliateOrgsACL.concat(ALL_WILDCARD),
        VIEW: adminFhirOrganizationsAffiliateOrgsACL.concat(VIEW),
        ADD: adminFhirOrganizationsAffiliateOrgsACL.concat(ADD),
        EDIT: adminFhirOrganizationsAffiliateOrgsACL.concat(EDIT),
      },
      TRANSFER_PATIENT: {
        VIEW: adminFhirOrganizationsTransferPatientACL.concat(VIEW),
      },
      OWN_ORGANIZATION: {
        ALL: adminFhirOrganizationsOwnOrgACL.concat(ALL_WILDCARD),
        VIEW: adminFhirOrganizationsOwnOrgACL.concat(VIEW),
      },
      IDENTIFIER_DETAILS: {
        ALL: adminFhirOrganizationsIdentifierDetails.concat(ALL_WILDCARD),
        VIEW: adminFhirOrganizationsIdentifierDetails.concat(VIEW),
        ADD: adminFhirOrganizationsIdentifierDetails.concat(ADD),
        EDIT: adminFhirOrganizationsIdentifierDetails.concat(EDIT),
        DELETE: adminFhirOrganizationsIdentifierDetails.concat(DELETE),
        HIDDEN: {
          VIEW: adminFhirOrganizationsIdentifierDetailsHidden.concat(VIEW),
          EDIT: adminFhirOrganizationsIdentifierDetailsHidden.concat(EDIT),
          DELETE: adminFhirOrganizationsIdentifierDetailsHidden.concat(DELETE),
        },
        EXTERNAL: {
          VIEW: adminFhirOrganizationsIdentifierDetailsExternal.concat(VIEW),
          DELETE: adminFhirOrganizationsIdentifierDetailsExternal.concat(DELETE),
          ADD: adminFhirOrganizationsIdentifierDetailsExternal.concat(ADD),
          EDIT: adminFhirOrganizationsIdentifierDetailsExternal.concat(EDIT),
        },
      },
      ADDRESS_DETAILS: {
        ADD: adminFhirOrganizationsAddressDetails.concat(ADD),
        EDIT: adminFhirOrganizationsAddressDetails.concat(EDIT),
        DELETE: adminFhirOrganizationsAddressDetails.concat(DELETE),
        ADD_HOUSING_STATUS: adminFhirOrganizationsAddressDetails.concat('addHousingStatus'),
      },
      CONTACT_DETAILS: {
        ADD: adminFhirOrganizationsContactDetails.concat(ADD),
        EDIT: adminFhirOrganizationsContactDetails.concat(EDIT),
        DELETE: adminFhirOrganizationsContactDetails.concat(DELETE),
      },
      CONTACTS: {
        ALL: adminFhirOrganizationsContacts.concat(ALL_WILDCARD),
        ADD: adminFhirOrganizationsContacts.concat(ADD),
        EDIT: adminFhirOrganizationsContacts.concat(EDIT),
        DELETE: adminFhirOrganizationsContacts.concat(VIEW),
      },
    },
    PERMISSIONS: {
      ALL: adminPermissionsACL.concat(ALL_WILDCARD),
      MENU: adminPermissionsACL.concat(MENU),
      ADD: adminPermissionsACL.concat(ADD),
      DELETE: adminPermissionsACL.concat(DELETE),
      EDIT: adminPermissionsACL.concat(EDIT),
    },
    WORKFLOW: {
      ALL: adminWorkflowACL.concat(ALL_WILDCARD),
      MENU: adminWorkflowACL.concat(MENU),
      VIEW: adminWorkflowACL.concat(VIEW),
      ADD: adminWorkflowACL.concat(ADD),
      EDIT: adminWorkflowACL.concat(EDIT),
      DELETE: adminWorkflowACL.concat(DELETE),
    },
    CHECKLIST: {
      ALL: adminChecklistACL.concat(ALL_WILDCARD),
      MENU: adminChecklistACL.concat(MENU),
      VIEW: adminChecklistACL.concat(VIEW),
      ADD: adminChecklistACL.concat(ADD),
      EDIT: adminChecklistACL.concat(EDIT),
      DELETE: adminChecklistACL.concat(DELETE),
    },
    CUSTOM_FIELDS: {
      ALL: customFieldsACL.concat(ALL_WILDCARD),
      MENU: customFieldsACL.concat(MENU),
      ADD: customFieldsACL.concat(ADD),
      DELETE: customFieldsACL.concat(DELETE),
      EDIT: customFieldsACL.concat(EDIT),
    },
  },
  CRS: {
    ALL: crsACL.concat(ALL_WILDCARD),
    DASHBOARD: {
      ALL: crsDashboardACL.concat(ALL_WILDCARD),
      MENU: crsDashboardACL.concat(MENU),
      DASHLET: {
        ALL: crsDashboardDashletACL.concat(ALL_WILDCARD),
        MY_TASKS: crsDashboardDashletACL.concat('myTasks', VIEW),
        MY_CASES: crsDashboardDashletACL.concat('myCases', VIEW),
        MY_PATIENTS: crsDashboardDashletACL.concat('myPatients', VIEW),
        MY_PATIENT_ENGAGEMENTS_ALL: crsDashletMyPatientEngagementsACL.concat(ALL_WILDCARD),
        MY_PATIENT_ENGAGEMENTS: crsDashletMyPatientEngagementsACL.concat(VIEW),
        MY_ORGANIZATION_PATIENTS_ALL: crsDashletMyOrganizationPatientsACL.concat(ALL_WILDCARD),
        MY_ORGANIZATION_PATIENTS: crsDashletMyOrganizationPatientsACL.concat(VIEW),
        ORPHAN_TASKS: crsDashletOrphanTasksACL.concat(VIEW),
        ORPHAN_GOALS: crsDashletOrphanGoalsACL.concat(VIEW),
      },
    },
    PATIENT: {
      ALL: crsPatientACL.concat(ALL_WILDCARD),
      MENU: crsPatientACL.concat(MENU),
      ADD: crsPatientACL.concat(ADD),
      MANUAL_ADD: crsPatientACL.concat(MANUAL_ADD),
      VIEW: crsPatientACL.concat(VIEW),
      TABS: {
        ALL: crsPatientTabsACL.concat(ALL_WILDCARD),
        GENERAL: {
          ALL: crsPatientTabsACL.concat('general', ALL_WILDCARD),
          VIEW: crsPatientTabsACL.concat('general', VIEW),
        },
        REFERRAL_CASES: {
          ALL: crsPatientTabsACL.concat('referralAndCases', ALL_WILDCARD),
          VIEW: crsPatientTabsACL.concat('referralAndCases', VIEW),
        },
        HEALTH: {
          ALL: crsPatientTabsACL.concat('health', ALL_WILDCARD),
          VIEW: crsPatientTabsACL.concat('health', VIEW),
        },
        GROUPS: {
          ALL: crsPatientTabsACL.concat('groups', ALL_WILDCARD),
          VIEW: crsPatientTabsACL.concat('groups', VIEW),
        },
        INSURANCE: {
          ALL: crsPatientTabsACL.concat('insurance', ALL_WILDCARD),
          VIEW: crsPatientTabsACL.concat('insurance', VIEW),
        },
        DOCUMENTS: {
          ALL: crsPatientTabsACL.concat('documents', ALL_WILDCARD),
          VIEW: crsPatientTabsACL.concat('documents', VIEW),
        },
        TASKS: {
          ALL: crsPatientTabsACL.concat('tasks', ALL_WILDCARD),
          VIEW: crsPatientTabsACL.concat('tasks', VIEW),
        },
        ENGAGEMENT_HUB: {
          ALL: crsPatientTabsACL.concat('engagement_hub', ALL_WILDCARD),
          VIEW: crsPatientTabsACL.concat('engagement_hub', VIEW),
        },
        CONSENTS: {
          ALL: crsPatientTabsACL.concat('consents', ALL_WILDCARD),
          VIEW: crsPatientTabsACL.concat('consents', VIEW),
        },
      },
      CARD: {
        ALL: crsPatientCardACL.concat(ALL_WILDCARD),
        VIEW: crsPatientCardACL.concat(VIEW),
      },
      SIL: {
        ALL: crsPatientSILACL.concat(ALL_WILDCARD),
        VIEW: crsPatientSILACL.concat(VIEW),
      },
      PRS: {
        ALL: crsPatientPRSACL.concat(ALL_WILDCARD),
        VIEW: crsPatientPRSACL.concat(VIEW),
      },
      INCKELIGIBLE: {
        ALL: crsPatientInckeligibleACL.concat(ALL_WILDCARD),
        VIEW: crsPatientInckeligibleACL.concat(VIEW),
      },
      NAT_TASK: {
        ALL: crsPatientNATTaskACL.concat(ALL_WILDCARD),
        ANSWER_TASK: crsPatientNATTaskACL.concat('answerTask'),
        PRINT_TASK: crsPatientNATTaskACL.concat('printTask'),
        ENROLL: crsPatientNATTaskACL.concat('enroll'),
        CLOSE_REQUEST: crsPatientNATTaskACL.concat('closeRequest'),
        CANCEL_REQUEST: crsPatientNATTaskACL.concat('cancelRequest'),
      },
      GROUPS: {
        ALL: crsPatientGroupsACL.concat(ALL_WILDCARD),
        VIEW: crsPatientGroupsACL.concat(VIEW),
        ADD: crsPatientGroupsACL.concat(ADD),
        EDIT: crsPatientGroupsACL.concat(EDIT),
        DELETE: crsPatientGroupsACL.concat(DELETE),
        MEMBERS: {
          ALL: crsPatientGroupsMembersACL.concat(ALL_WILDCARD),
          ADD: crsPatientGroupsMembersACL.concat(ADD),
          EDIT: crsPatientGroupsMembersACL.concat(EDIT),
          DELETE: crsPatientGroupsMembersACL.concat(DELETE),
          VIEW_MORE: crsPatientGroupsMembersACL.concat('viewMore'),
        },
      },
      TASKS: getACLs<TaskACLs>({
        ALL: crsPatientTasksACL.concat(ALL_WILDCARD),
        VIEW: crsPatientTasksACL.concat(VIEW),
        ADD: crsPatientTasksACL.concat(ADD),
        EDIT: crsPatientTasksACL.concat(EDIT),
        HANDLER: {
          ALL: crsPatientTasksHandlerACL.concat(ALL_WILDCARD),
          REFERRAL_INTERNAL: crsPatientTasksHandlerACL.concat('referralInternal'),
          REFERRAL_EXTERNAL: crsPatientTasksHandlerACL.concat('referralExternal'),
          WARM_HANDOFF: crsPatientTasksHandlerACL.concat('warmHandoff'),
          APPOINTMENT: crsPatientTasksHandlerACL.concat('appointment'),
          ASSIGN_PROGRAM: crsPatientTasksHandlerACL.concat(ASSIGN_PROGRAM),
        },
        NOTES: getACLs<NotesACLs>({
          ALL: crsPatientTasksNotesACL.concat(ALL_WILDCARD),
          VIEW: crsPatientTasksNotesACL.concat(VIEW),
          ADD: crsPatientTasksNotesACL.concat(ADD),
        }),
        INPUT_OUTPUT: {
          ALL: crsPatientTasksInputOutputACL.concat(ALL_WILDCARD),
          VIEW: crsPatientTasksInputOutputACL.concat(VIEW),
          EDIT: crsPatientTasksInputOutputACL.concat(EDIT),
          DELETE: crsPatientTasksInputOutputACL.concat(DELETE),
        },
      }),
      NOTES: getACLs<NotesACLs>({
        ALL: crsPatientNotesACL.concat(ALL_WILDCARD),
        VIEW: crsPatientNotesACL.concat(VIEW),
        ADD: crsPatientNotesACL.concat(ADD),
      }),
      ATTACHMENTS: getACLs<AttachmentsACLs>({
        ALL: crsPatientAttachmentsACL.concat(ALL_WILDCARD),
        VIEW: crsPatientAttachmentsACL.concat(VIEW),
        ADD: crsPatientAttachmentsACL.concat(ADD),
        EDIT: crsPatientAttachmentsACL.concat(EDIT),
      }),
      CARE_TEAMS: getACLs<CareTeamACLs>({
        ALL: crsPatientCareTeamsACL.concat(ALL_WILDCARD),
        VIEW: crsPatientCareTeamsACL.concat(VIEW),
        ADD: crsPatientCareTeamsACL.concat(ADD),
        EDIT: crsPatientCareTeamsACL.concat(EDIT),
        MEMBERS: {
          ALL: crsPatientCareTeamsMembersACL.concat(ALL_WILDCARD),
          ADD: crsPatientCareTeamsMembersACL.concat(ADD),
          EDIT: crsPatientCareTeamsMembersACL.concat(EDIT),
          DELETE: crsPatientCareTeamsMembersACL.concat(DELETE),
        },
      }),
      CONTACTS: {
        ALL: crsPatientContactsACL.concat(ALL_WILDCARD),
        VIEW: crsPatientContactsACL.concat(VIEW),
        ADD: crsPatientContactsACL.concat(ADD),
        EDIT: crsPatientContactsACL.concat(EDIT),
      },
      RELATED_PERSONS: {
        ALL: crsPatientRelatedPersonsACL.concat(ALL_WILDCARD),
        VIEW: crsPatientRelatedPersonsACL.concat(VIEW),
        ADD: crsPatientRelatedPersonsACL.concat(ADD),
        EDIT: crsPatientRelatedPersonsACL.concat(EDIT),
        DELETE: crsPatientRelatedPersonsACL.concat(DELETE),
        ADD_HOUSING_STATUS: crsPatientRelatedPersonsACL.concat('addHousingStatus'),
      },
      CONDITION: {
        ALL: crsPatientConditionACL.concat(ALL_WILDCARD),
        VIEW: crsPatientConditionACL.concat(VIEW),
        ADD: crsPatientConditionACL.concat(ADD),
        EDIT: crsPatientConditionACL.concat(EDIT),
      },
      EPISODE_OF_CARE: {
        ALL: crsPatientEpisodeOfCareACL.concat(ALL_WILDCARD),
        VIEW: crsPatientEpisodeOfCareACL.concat(VIEW),
        ADD: crsPatientEpisodeOfCareACL.concat(ADD),
        EDIT: crsPatientEpisodeOfCareACL.concat(EDIT),
      },
      APPOINTMENT: {
        ALL: crsPatientAppointmentACL.concat(ALL_WILDCARD),
        VIEW: crsPatientAppointmentACL.concat(VIEW),
        ADD: crsPatientAppointmentACL.concat(ADD),
        EDIT: crsPatientAppointmentACL.concat(EDIT),
      },
      OBSERVATION: {
        ALL: crsPatientObservationACL.concat(ALL_WILDCARD),
        VIEW: crsPatientObservationACL.concat(VIEW),
        ADD: crsPatientObservationACL.concat(ADD),
        EDIT: crsPatientObservationACL.concat(EDIT),
      },
      IMMUNIZATION: {
        ALL: crsPatientImmunizationACL.concat(ALL_WILDCARD),
        VIEW: crsPatientImmunizationACL.concat(VIEW),
        ADD: crsPatientImmunizationACL.concat(ADD),
      },
      CASES: {
        ALL: crsPatientCasesACL.concat(ALL_WILDCARD),
        VIEW: crsPatientCasesACL.concat(VIEW),
        EDIT: crsPatientCasesACL.concat(EDIT),
      },
      CARE_PLAN: {
        ALL: crsPatientCarePlanACL.concat(ALL_WILDCARD),
        VIEW: crsPatientCarePlanACL.concat(VIEW),
        EDIT: crsPatientCarePlanACL.concat(EDIT),
      },
      DEVICE: {
        ALL: crsPatientDeviceACL.concat(ALL_WILDCARD),
        VIEW: crsPatientDeviceACL.concat(VIEW),
        ADD: crsPatientDeviceACL.concat(ADD),
        EDIT: crsPatientDeviceACL.concat(EDIT),
      },
      SERVICE_REQUESTS: {
        ALL: crsPatientServiceRequestACL.concat(ALL_WILDCARD),
        VIEW: crsPatientServiceRequestACL.concat(VIEW),
        EDIT: crsPatientServiceRequestACL.concat(EDIT),
        ADD: crsPatientServiceRequestACL.concat(ADD),
        TASKS: getACLs<TaskACLs>({
          ALL: crsPatientServiceRequestTasksACL.concat(ALL_WILDCARD),
          VIEW: crsPatientServiceRequestTasksACL.concat(VIEW),
          ADD: crsPatientServiceRequestTasksACL.concat(ADD),
          EDIT: crsPatientServiceRequestTasksACL.concat(EDIT),
          HANDLER: {
            ALL: crsPatientServiceRequestTasksHandlerACL.concat(ALL_WILDCARD),
            REFERRAL_INTERNAL: crsPatientServiceRequestTasksHandlerACL.concat('referralInternal'),
            REFERRAL_EXTERNAL: crsPatientServiceRequestTasksHandlerACL.concat('referralExternal'),
            WARM_HANDOFF: crsPatientServiceRequestTasksHandlerACL.concat('warmHandoff'),
            APPOINTMENT: crsPatientServiceRequestTasksHandlerACL.concat('appointment'),
            ASSIGN_PROGRAM: crsPatientServiceRequestTasksHandlerACL.concat(ASSIGN_PROGRAM),
          },
          NOTES: getACLs<NotesACLs>({
            ALL: crsPatientServiceRequestTasksNotesACL.concat(ALL_WILDCARD),
            VIEW: crsPatientServiceRequestTasksNotesACL.concat(VIEW),
            ADD: crsPatientServiceRequestTasksNotesACL.concat(ADD),
          }),
          INPUT_OUTPUT: {
            ALL: crsPatientServiceRequestTasksInputOutputACL.concat(ALL_WILDCARD),
            VIEW: crsPatientServiceRequestTasksInputOutputACL.concat(VIEW),
            EDIT: crsPatientServiceRequestTasksInputOutputACL.concat(EDIT),
            DELETE: crsPatientServiceRequestTasksInputOutputACL.concat(DELETE),
          },
        }),
        NOTES: getACLs<NotesACLs>({
          ALL: crsPatientServiceRequestNotesACL.concat(ALL_WILDCARD),
          VIEW: crsPatientServiceRequestNotesACL.concat(VIEW),
          ADD: crsPatientServiceRequestNotesACL.concat(ADD),
        }),
      },
      SEARCH: {
        ALL: crsPatientSearchACL.concat(ALL_WILDCARD),
        SIMPLE: crsPatientSearchACL.concat('simple'),
        ADVANCED: crsPatientSearchACL.concat('advanced'),
      },
      PERSONAL_DETAILS: {
        ALL: crsPatientPersonalDetailsACL.concat(ALL_WILDCARD),
        VIEW: crsPatientPersonalDetailsACL.concat(VIEW),
        EDIT: crsPatientPersonalDetailsACL.concat(EDIT),
      },
      MANAGING_ORGANIZATION: {
        ALL: crsPatientManagingOrganizationACL.concat(ALL_WILDCARD),
        EDIT: crsPatientManagingOrganizationACL.concat(EDIT),
      },
      CONTACT_DETAILS: {
        ALL: crsPatientContactDetailsACL.concat(ALL_WILDCARD),
        VIEW: crsPatientContactDetailsACL.concat(VIEW),
        ADD: crsPatientContactDetailsACL.concat(ADD),
        EDIT: crsPatientContactDetailsACL.concat(EDIT),
        DELETE: crsPatientContactDetailsACL.concat(DELETE),
      },
      ADDRESS_DETAILS: {
        ALL: crsPatientAddressDetailsACL.concat(ALL_WILDCARD),
        VIEW: crsPatientAddressDetailsACL.concat(VIEW),
        ADD: crsPatientAddressDetailsACL.concat(ADD),
        EDIT: crsPatientAddressDetailsACL.concat(EDIT),
        DELETE: crsPatientAddressDetailsACL.concat(DELETE),
        ADD_HOUSING_STATUS: crsPatientAddressDetailsACL.concat('addHousingStatus'),
      },
      IDENTIFIER_DETAILS: {
        ALL: crsPatientIdentifierDetailsACL.concat(ALL_WILDCARD),
        VIEW: crsPatientIdentifierDetailsACL.concat(VIEW),
        ADD: crsPatientIdentifierDetailsACL.concat(ADD),
        EDIT: crsPatientIdentifierDetailsACL.concat(EDIT),
        DELETE: crsPatientIdentifierDetailsACL.concat(DELETE),
        HIDDEN: {
          ALL: crsPatientIdentifierDetailsACLHidden.concat(ALL_WILDCARD),
          VIEW: crsPatientIdentifierDetailsACLHidden.concat(VIEW),
          EDIT: crsPatientIdentifierDetailsACLHidden.concat(EDIT),
          DELETE: crsPatientIdentifierDetailsACLHidden.concat(DELETE),
        },
        EXTERNAL: {
          VIEW: crsPatientIdentifierDetailsACLExternal.concat(VIEW),
          DELETE: crsPatientIdentifierDetailsACLExternal.concat(DELETE),
          ADD: crsPatientIdentifierDetailsACLExternal.concat(ADD),
          EDIT: crsPatientIdentifierDetailsACLExternal.concat(EDIT),
        },
      },
      ASSESSMENTS: {
        ALL: crsPatientAssessmentsACL.concat(ALL_WILDCARD),
        START: crsPatientAssessmentsACL.concat('start'),
        VIEW: crsPatientAssessmentsACL.concat(VIEW),
      },
      INSURANCE: {
        ALL: crsPatientInsuranceACL.concat(ALL_WILDCARD),
        ADD: crsPatientInsuranceACL.concat(ADD),
        EDIT: crsPatientInsuranceACL.concat(EDIT),
      },
      GENERAL_PRACTITIONER: {
        VIEW: crsPatientGeneralPractitionerACL.concat(VIEW),
        ADD: crsPatientGeneralPractitionerACL.concat(ADD),
        DELETE: crsPatientGeneralPractitionerACL.concat(DELETE),
      },
      DEACTIVATE: crsPatientACL.concat('deactivate'),
      CONSENT: {
        ALL: crsPatientConsentACL.concat(ALL_WILDCARD),
        ADD: crsPatientConsentACL.concat(ADD),
        EDIT: crsPatientConsentACL.concat(EDIT),
        VIEW: crsPatientConsentACL.concat(VIEW),
      },
      LINK: {
        ALL: crsPatientLinkACL.concat(ALL_WILDCARD),
        VIEW: crsPatientACL.concat(VIEW),
      },
      CUSTOM_FIELDS: {
        ALL: crsPatientCustomFieldsDetailsACL.concat(ALL_WILDCARD),
        VIEW: crsPatientCustomFieldsDetailsACL.concat(VIEW),
        EDIT: crsPatientCustomFieldsDetailsACL.concat(EDIT),
      },
      BASIC_INFORMATION: {
        ALL: crsPatientBasicInformationACL.concat(ALL_WILDCARD),
        VIEW_EOC: crsPatientBasicInformationACL.concat('viewEOC'),
        VIEW_TAGS: crsPatientBasicInformationACL.concat('viewTags'),
      },
    },
    REFERRAL: {
      ALL: crsReferralACL.concat(ALL_WILDCARD),
      MENU: crsReferralACL.concat(MENU),
      ADD: crsReferralACL.concat(ADD),
      EDIT: crsReferralACL.concat(EDIT),
      IDENTIFIER_DETAILS: {
        ALL: crsReferralIdentifierDetailsACL.concat(ALL_WILDCARD),
        VIEW: crsReferralIdentifierDetailsACL.concat(VIEW),
        ADD: crsReferralIdentifierDetailsACL.concat(ADD),
        EDIT: crsReferralIdentifierDetailsACL.concat(EDIT),
        DELETE: crsReferralIdentifierDetailsACL.concat(DELETE),
        HIDDEN: {
          VIEW: crsReferralIdentifierDetailsACLHidden.concat(VIEW),
          EDIT: crsReferralIdentifierDetailsACLHidden.concat(EDIT),
          DELETE: crsReferralIdentifierDetailsACLHidden.concat(DELETE),
        },
      },
      WORKFLOW: getACLs<WorkflowACLs>({
        CLOSE: crsReferralWorkflowACL.concat('close'),
        NEXT: crsReferralWorkflowACL.concat('next'),
        VIEW: crsReferralWorkflowACL.concat(VIEW),
        OWNER: crsReferralWorkflowACL.concat('owner'),
      }),
      CHILD_REFERRAL: {
        ALL: crsChildReferralACL.concat(ALL_WILDCARD),
        EDIT: crsChildReferralACL.concat(EDIT),
      },
      CONSENT: {
        ALL: crsReferralConsentACL.concat(ALL_WILDCARD),
        VIEW: crsReferralConsentACL.concat(VIEW),
        ADD: crsReferralConsentACL.concat(ADD),
        EDIT: crsReferralConsentACL.concat(EDIT),
      },
      CONTACT_ATTEMPTS: {
        ALL: crsReferralContactAttemptsACL.concat(ALL_WILDCARD),
        VIEW: crsReferralContactAttemptsACL.concat(VIEW),
        ADD: crsReferralContactAttemptsACL.concat(ADD),
      },
      OBSERVATION: {
        ALL: crsReferralObservationACL.concat(ALL_WILDCARD),
        VIEW: crsReferralObservationACL.concat(VIEW),
        ADD: crsReferralObservationACL.concat(ADD),
        EDIT: crsReferralObservationACL.concat(EDIT),
      },
      TASKS: getACLs<TaskACLs>({
        ALL: crsReferralTaskACL.concat(ALL_WILDCARD),
        VIEW: crsReferralTaskACL.concat(VIEW),
        ADD: crsReferralTaskACL.concat(ADD),
        EDIT: crsReferralTaskACL.concat(EDIT),
        HANDLER: {
          ALL: crsReferralTaskHandlerAcl.concat(ALL_WILDCARD),
          REFERRAL_INTERNAL: crsReferralTaskHandlerAcl.concat('referralInternal'),
          REFERRAL_EXTERNAL: crsReferralTaskHandlerAcl.concat('referralExternal'),
          WARM_HANDOFF: crsReferralTaskHandlerAcl.concat('warmHandoff'),
          APPOINTMENT: crsReferralTaskHandlerAcl.concat('appointment'),
          ASSIGN_PROGRAM: crsReferralTaskHandlerAcl.concat(ASSIGN_PROGRAM),
        },
        NOTES: getACLs<NotesACLs>({
          ALL: crsReferralTaskNotesACL.concat(ALL_WILDCARD),
          VIEW: crsReferralTaskNotesACL.concat(VIEW),
          ADD: crsReferralTaskNotesACL.concat(ADD),
        }),
        INPUT_OUTPUT: {
          ALL: crsReferralTaskInputOutputACL.concat(ALL_WILDCARD),
          VIEW: crsReferralTaskInputOutputACL.concat(VIEW),
          EDIT: crsReferralTaskInputOutputACL.concat(EDIT),
          DELETE: crsReferralTaskInputOutputACL.concat(DELETE),
        },
      }),
    },
    CASE: {
      ALL: crsCaseACL.concat(ALL_WILDCARD),
      VIEW: crsCaseACL.concat(VIEW),
      MENU: crsCaseACL.concat(MENU),
      EDIT: crsCaseACL.concat(EDIT),
      GOAL: {
        ALL: crsCaseGoalACL.concat(ALL_WILDCARD),
        VIEW: crsCaseGoalACL.concat(VIEW),
        ADD: crsCaseGoalACL.concat(ADD),
        EDIT: crsCaseGoalACL.concat(EDIT),
        NOTES: getACLs<NotesACLs>({
          ALL: crsCaseGoalNotesACL.concat(ALL_WILDCARD),
          VIEW: crsCaseGoalNotesACL.concat(VIEW),
          ADD: crsCaseGoalNotesACL.concat(ADD),
        }),
      },
      TASKS: getACLs<TaskACLs>({
        ALL: crsCaseTaskACL.concat(ALL_WILDCARD),
        VIEW: crsCaseTaskACL.concat(VIEW),
        ADD: crsCaseTaskACL.concat(ADD),
        EDIT: crsCaseTaskACL.concat(EDIT),
        HANDLER: {
          ALL: crsCaseTaskHandlerAcl.concat(ALL_WILDCARD),
          REFERRAL_INTERNAL: crsCaseTaskHandlerAcl.concat('referralInternal'),
          REFERRAL_EXTERNAL: crsCaseTaskHandlerAcl.concat('referralExternal'),
          WARM_HANDOFF: crsCaseTaskHandlerAcl.concat('warmHandoff'),
          APPOINTMENT: crsCaseTaskHandlerAcl.concat('appointment'),
          ASSIGN_PROGRAM: crsCaseTaskHandlerAcl.concat(ASSIGN_PROGRAM),
        },
        NOTES: getACLs<NotesACLs>({
          ALL: crsCaseTaskNotesACL.concat(ALL_WILDCARD),
          VIEW: crsCaseTaskNotesACL.concat(VIEW),
          ADD: crsCaseTaskNotesACL.concat(ADD),
        }),
        INPUT_OUTPUT: {
          ALL: crsCaseTaskInputOutputACL.concat(ALL_WILDCARD),
          VIEW: crsCaseTaskInputOutputACL.concat(VIEW),
          EDIT: crsCaseTaskInputOutputACL.concat(EDIT),
          DELETE: crsCaseTaskInputOutputACL.concat(DELETE),
        },
      }),
      CARE_TEAMS: getACLs<CareTeamACLs>({
        ALL: crsCaseCareTeamsACL.concat(ALL_WILDCARD),
        VIEW: crsCaseCareTeamsACL.concat(VIEW),
        ADD: crsCaseCareTeamsACL.concat(ADD),
        EDIT: crsCaseCareTeamsACL.concat(EDIT),
        MEMBERS: {
          ALL: crsCaseCareTeamsMembersACL.concat(ALL_WILDCARD),
          ADD: crsCaseCareTeamsMembersACL.concat(ADD),
          EDIT: crsCaseCareTeamsMembersACL.concat(EDIT),
          DELETE: crsCaseCareTeamsMembersACL.concat(DELETE),
        },
      }),
      CONTACT_ATTEMPTS: {
        ALL: crsCaseContactAttemptsACL.concat(ALL_WILDCARD),
        VIEW: crsCaseContactAttemptsACL.concat(VIEW),
        ADD: crsCaseContactAttemptsACL.concat(ADD),
      },
      NOTES: getACLs<NotesACLs>({
        ALL: crsCaseNotesACL.concat(ALL_WILDCARD),
        VIEW: crsCaseNotesACL.concat(VIEW),
        ADD: crsCaseNotesACL.concat(ADD),
      }),
      ATTACHMENTS: getACLs<AttachmentsACLs>({
        ALL: crsCaseAttachmentsACL.concat(ALL_WILDCARD),
        VIEW: crsCaseAttachmentsACL.concat(VIEW),
        ADD: crsCaseAttachmentsACL.concat(ADD),
        EDIT: crsCaseAttachmentsACL.concat(EDIT),
      }),
      WORKFLOW: getACLs<WorkflowACLs>({
        ALL: crsCaseWorkflowACL.concat(ALL_WILDCARD),
        CLOSE: crsCaseWorkflowACL.concat('close'),
        NEXT: crsCaseWorkflowACL.concat('next'),
        VIEW: crsCaseWorkflowACL.concat(VIEW),
        OWNER: crsCaseWorkflowACL.concat('owner'),
      }),
      STATISTICS: {
        ALL: crsCaseStatisticsACL.concat(ALL_WILDCARD),
        VIEW: crsCaseStatisticsACL.concat(VIEW),
      },
      SERVICE_REQUESTS: {
        ALL: crsCaseServiceRequestACL.concat(ALL_WILDCARD),
        VIEW: crsCaseServiceRequestACL.concat(VIEW),
        EDIT: crsCaseServiceRequestACL.concat(EDIT),
        ADD: crsCaseServiceRequestACL.concat(ADD),
        TASKS: getACLs<TaskACLs>({
          ALL: crsCaseServiceRequestTasksACL.concat(ALL_WILDCARD),
          VIEW: crsCaseServiceRequestTasksACL.concat(VIEW),
          ADD: crsCaseServiceRequestTasksACL.concat(ADD),
          EDIT: crsCaseServiceRequestTasksACL.concat(EDIT),
          HANDLER: {
            ALL: crsCaseServiceRequestTasksHandlerACL.concat(ALL_WILDCARD),
            REFERRAL_INTERNAL: crsCaseServiceRequestTasksHandlerACL.concat('referralInternal'),
            REFERRAL_EXTERNAL: crsCaseServiceRequestTasksHandlerACL.concat('referralExternal'),
            WARM_HANDOFF: crsCaseServiceRequestTasksHandlerACL.concat('warmHandoff'),
            APPOINTMENT: crsCaseServiceRequestTasksHandlerACL.concat('appointment'),
            ASSIGN_PROGRAM: crsCaseServiceRequestTasksHandlerACL.concat(ASSIGN_PROGRAM),
          },
          NOTES: getACLs<NotesACLs>({
            ALL: crsCaseServiceRequestTasksNotesACL.concat(ALL_WILDCARD),
            VIEW: crsCaseServiceRequestTasksNotesACL.concat(VIEW),
            ADD: crsCaseServiceRequestTasksNotesACL.concat(ADD),
          }),
          INPUT_OUTPUT: {
            ALL: crsCaseServiceRequestTasksInputOutputACL.concat(ALL_WILDCARD),
            VIEW: crsCaseServiceRequestTasksInputOutputACL.concat(VIEW),
            EDIT: crsCaseServiceRequestTasksInputOutputACL.concat(EDIT),
            DELETE: crsCaseServiceRequestTasksInputOutputACL.concat(DELETE),
          },
        }),
        NOTES: getACLs<NotesACLs>({
          ALL: crsCaseServiceRequestNotesACL.concat(ALL_WILDCARD),
          VIEW: crsCaseServiceRequestNotesACL.concat(VIEW),
          ADD: crsCaseServiceRequestNotesACL.concat(ADD),
        }),
      },
      SUMMARY: {
        VIEW: crsCaseSummaryACL.concat(VIEW),
      },
      DETAILS: {
        ALL: crsCaseDetailsACL.concat(ALL_WILDCARD),
        VIEW: crsCaseDetailsACL.concat(VIEW),
        PRINTABLE_CARE_PLAN: crsCaseDetailsACL.concat('printableCarePlan'),
      },
      COMPLEMENTARY_INFO: {
        VIEW: crsCaseComplementaryInfoACL.concat(VIEW),
      },
      RELATED_REFERRALS: {
        ALL: crsRelatedReferralsACL.concat(ALL_WILDCARD),
        VIEW: crsRelatedReferralsACL.concat(VIEW),
      },
      CONSENT: {
        ALL: crsPatientConsentACL.concat(ALL_WILDCARD),
        ADD: crsPatientConsentACL.concat(ADD),
        EDIT: crsPatientConsentACL.concat(EDIT),
        VIEW: crsPatientConsentACL.concat(VIEW),
      },
      OUTBOUND_REFERRAL: {
        ALL: crsCaseOutboundReferralACL.concat(ALL_WILDCARD),
        VIEW: crsCaseOutboundReferralACL.concat(VIEW),
      },
      APPOINTMENT: {
        ALL: crsCaseAppointmentACL.concat(ALL_WILDCARD),
        VIEW: crsCaseAppointmentACL.concat(VIEW),
        ADD: crsCaseAppointmentACL.concat(ADD),
        EDIT: crsCaseAppointmentACL.concat(EDIT),
      },
      PATIENT: {
        ALL: crsCasePatientACL.concat(ALL_WILDCARD),
        VIEW: crsCasePatientACL.concat(VIEW),
      },
      ASSESSMENT: {
        ALL: crsCaseAssessmentACL.concat(ALL_WILDCARD),
        VIEW: crsCaseAssessmentACL.concat(VIEW),
        START: crsCaseAssessmentACL.concat('start'),
      },
      OBSERVATION: {
        ALL: crsCaseObservationACL.concat(ALL_WILDCARD),
        VIEW: crsCaseObservationACL.concat(VIEW),
        ADD: crsCaseObservationACL.concat(ADD),
        EDIT: crsCaseObservationACL.concat(EDIT),
      },
      CONDITION: {
        ALL: crsCaseConditionACL.concat(ALL_WILDCARD),
        VIEW: crsCaseConditionACL.concat(VIEW),
      },
      FAMILY_MEMBERS: {
        ALL: crsCaseFamilyMembersACL.concat(ALL_WILDCARD),
        VIEW: crsCaseFamilyMembersACL.concat(VIEW),
      },
      RELATED_PERSONS: {
        ALL: crsCaseRelatedPersonsACL.concat(ALL_WILDCARD),
        VIEW: crsCaseRelatedPersonsACL.concat(VIEW),
      },
      GROUPS: {
        ALL: crsCaseGroupsACL.concat(ALL_WILDCARD),
        VIEW: crsCaseGroupsACL.concat(VIEW),
        ADD: crsCaseGroupsACL.concat(ADD),
        EDIT: crsCaseGroupsACL.concat(EDIT),
        DELETE: crsCaseGroupsACL.concat(DELETE),
        MEMBERS: {
          ALL: crsCaseGroupsMemberACL.concat(ALL_WILDCARD),
          ADD: crsCaseGroupsMemberACL.concat(ADD),
          EDIT: crsCaseGroupsMemberACL.concat(EDIT),
          DELETE: crsCaseGroupsMemberACL.concat(DELETE),
          VIEW_MORE: crsCaseGroupsMemberACL.concat('viewMore'),
        },
      },
    },
    TASK: {
      ALL: crsTaskACL.concat(ALL_WILDCARD),
      MENU: crsTaskACL.concat(MENU),
      EDIT: crsTaskACL.concat(EDIT),
      notes: {
        VIEW: crsTaskNotesACL.concat(VIEW),
      },
    },
    PRACTITIONER: {
      ALL: crsPractitionerACL.concat(ALL_WILDCARD),
      MENU: crsPractitionerACL.concat(MENU),
      MANUAL_ADD: crsPractitionerACL.concat('manualAdd'),
      ADD: crsPractitionerACL.concat(ADD),
      VIEW: crsPractitionerACL.concat(VIEW),
      ROLES: {
        ADD: crsPractitionerRolesACL.concat(ADD),
        DELETE: crsPractitionerRolesACL.concat(DELETE),
      },
      PERSONAL_DETAILS: {
        EDIT: crsPractitionerPersonalDetailsACL.concat(EDIT),
      },
      CONTACT_DETAILS: {
        ADD: crsPractitionerContactDetailsACL.concat(ADD),
        EDIT: crsPractitionerContactDetailsACL.concat(EDIT),
        DELETE: crsPractitionerContactDetailsACL.concat(DELETE),
      },
      IDENTIFIER_DETAILS: {
        ADD: crsPractitionerIdentifierDetailsACL.concat(ADD),
        EDIT: crsPractitionerIdentifierDetailsACL.concat(EDIT),
        DELETE: crsPractitionerIdentifierDetailsACL.concat(DELETE),
        HIDDEN: {
          VIEW: crsPractitionerIdentifierDetailsACLHidden.concat(VIEW),
          EDIT: crsPractitionerIdentifierDetailsACLHidden.concat(EDIT),
          DELETE: crsPractitionerIdentifierDetailsACLHidden.concat(DELETE),
        },
        EXTERNAL: {
          VIEW: crsPractitionerIdentifierDetailsACLExternal.concat(VIEW),
          ADD: crsPractitionerIdentifierDetailsACLExternal.concat(ADD),
          EDIT: crsPractitionerIdentifierDetailsACLExternal.concat(EDIT),
          DELETE: crsPractitionerIdentifierDetailsACLExternal.concat(DELETE),
        },
      },
      LANGUAGE_DETAILS: {
        ADD: crsPractitionerLanguageDetailsACL.concat(ADD),
        EDIT: crsPractitionerLanguageDetailsACL.concat(EDIT),
        DELETE: crsPractitionerLanguageDetailsACL.concat(DELETE),
      },
      ADDRESS_DETAILS: {
        ADD: crsPractitionerAddressDetailsACL.concat(ADD),
        EDIT: crsPractitionerAddressDetailsACL.concat(EDIT),
        DELETE: crsPractitionerAddressDetailsACL.concat(DELETE),
        ADD_HOUSING_STATUS: crsPractitionerAddressDetailsACL.concat('addHousingStatus'),
      },
    },
    TAGS: {
      ALL: crsTagsACL.concat(ALL_WILDCARD),
      MENU: crsTagsACL.concat(MENU),
      VIEW: crsTagsACL.concat(VIEW),
      ADD: crsTagsACL.concat(ADD),
      EDIT: crsTagsACL.concat(EDIT),
      DELETE: crsTagsACL.concat(DELETE),
    },
  },
  YOCARE: {
    ALL: yoCareACL.concat(ALL_WILDCARD),
    USERS: {
      ALL: yoCareUsersACL.concat(ALL_WILDCARD),
      MENU: yoCareUsersACL.concat(MENU),
    },
    PERSONS: {
      ALL: yoCarePersonsACL.concat(ALL_WILDCARD),
      MENU: yoCarePersonsACL.concat(MENU),
    },
    DATASYSTEMS: {
      ALL: yoCareDataSystemsACL.concat(ALL_WILDCARD),
      MENU: yoCareDataSystemsACL.concat(MENU),
    },
  },
  ENGAGEMENT_HUB: {
    ALL: engagementHubACL.concat(ALL_WILDCARD),
    PATIENT_ENGAGEMENT: {
      ALL: engagementHubPatientEngagementACL.concat(ALL_WILDCARD),
      MENU: engagementHubPatientEngagementACL.concat(MENU),
      VIEW: engagementHubPatientEngagementACL.concat(VIEW),
      ADD: engagementHubPatientEngagementACL.concat(ADD),
      EDIT: engagementHubPatientEngagementACL.concat(EDIT),
      NOTES: getACLs<NotesACLs>({
        ALL: engagementHubPatientEngagementNotesACL.concat(ALL_WILDCARD),
        VIEW: engagementHubPatientEngagementNotesACL.concat(VIEW),
        ADD: engagementHubPatientEngagementNotesACL.concat(ADD),
      }),
      ATTACHMENTS: getACLs<AttachmentsACLs>({
        ALL: engagementHubPatientEngagementAttachmentsACL.concat(ALL_WILDCARD),
        VIEW: engagementHubPatientEngagementAttachmentsACL.concat(VIEW),
        ADD: engagementHubPatientEngagementAttachmentsACL.concat(ADD),
        EDIT: engagementHubPatientEngagementAttachmentsACL.concat(EDIT),
      }),
      WORKFLOW: getACLs<WorkflowACLs>({
        ALL: engagementHubPatientEngagementWorkflowACL.concat(ALL_WILDCARD),
        CLOSE: engagementHubPatientEngagementWorkflowACL.concat('close'),
        NEXT: engagementHubPatientEngagementWorkflowACL.concat('next'),
        VIEW: engagementHubPatientEngagementWorkflowACL.concat(VIEW),
        OWNER: engagementHubPatientEngagementWorkflowACL.concat('owner'),
      }),
      APPOINTMENT: {
        ALL: engagementHubPatientEngagementAppointmentACL.concat(ALL_WILDCARD),
        VIEW: engagementHubPatientEngagementAppointmentACL.concat(VIEW),
        ADD: engagementHubPatientEngagementAppointmentACL.concat(ADD),
        EDIT: engagementHubPatientEngagementAppointmentACL.concat(EDIT),
      },
      TASKS: {
        ALL: engagementHubPatientEngagementTasksACL.concat(ALL_WILDCARD),
        VIEW: engagementHubPatientEngagementTasksACL.concat(VIEW),
        ADD: engagementHubPatientEngagementTasksACL.concat(ADD),
        EDIT: engagementHubPatientEngagementTasksACL.concat(EDIT),
        NOTES: getACLs<NotesACLs>({
          ALL: engagementHubPatientEngagementTasksNotesACL.concat(ALL_WILDCARD),
          VIEW: engagementHubPatientEngagementTasksNotesACL.concat(VIEW),
          ADD: engagementHubPatientEngagementTasksNotesACL.concat(ADD),
        }),
        HANDLER: {
          ALL: engagementHubPatientEngagementTasksHandlerACL.concat(ALL_WILDCARD),
          REFERRAL_INTERNAL:
            engagementHubPatientEngagementTasksHandlerACL.concat('referralInternal'),
          REFERRAL_EXTERNAL:
            engagementHubPatientEngagementTasksHandlerACL.concat('referralExternal'),
          WARM_HANDOFF: engagementHubPatientEngagementTasksHandlerACL.concat('warmHandoff'),
          APPOINTMENT: engagementHubPatientEngagementTasksHandlerACL.concat('appointment'),
          ASSIGN_PROGRAM: engagementHubPatientEngagementTasksHandlerACL.concat(ASSIGN_PROGRAM),
        },
      },
      STATISTICS: {
        ALL: engagementHubPatientEngagementStatisticsACL.concat(ALL_WILDCARD),
        VIEW: engagementHubPatientEngagementStatisticsACL.concat(VIEW),
      },
    },
  },
  PROVIDER_WINDOW: {
    ALL: crsProviderWindowACL.concat(ALL_WILDCARD),
    VIEW: crsProviderWindowACL.concat(VIEW),
    OPEN_NON_EXISTENT_PATIENT: crsProviderWindowACL.concat('open_non_existent_patient'),
  },
  DATA_QUALITY: {
    ALL: dataQuality.concat(ALL_WILDCARD),
    INCIDENT: {
      ALL: dataQualityIncidentACL.concat(ALL_WILDCARD),
      MENU: dataQualityIncidentACL.concat(MENU),
      EDIT: dataQualityIncidentACL.concat(EDIT),
    },
  },
  NAT: {
    ALL: natAcl.concat(ALL_WILDCARD),
    SERVICE_REQUEST: {
      ALL: natServiceRequestACL.concat(ALL_WILDCARD),
      VIEW: natServiceRequestACL.concat(VIEW),
      ADD: natServiceRequestACL.concat(ADD),
      EDIT: natServiceRequestACL.concat(EDIT),
    },
    TASK: {
      ALL: natTaskAcl.concat(ALL_WILDCARD),
      VIEW: natTaskAcl.concat(VIEW),
      ADD: natTaskAcl.concat(ADD),
      EDIT: natTaskAcl.concat(EDIT),
      NOTES: getACLs<NotesACLs>({
        ALL: natTaskNotesACL.concat(ALL_WILDCARD),
        VIEW: natTaskNotesACL.concat(VIEW),
        ADD: natTaskNotesACL.concat(ADD),
      }),
    },
  },
};

export const getACLsTemplateList = (): [string, string][] => {
  const aclsList = getFlatACLsList(crsAcls);
  const actions = [ADD, EDIT, VIEW, DELETE];
  return aclsList.map((acl) => {
    let description = '';
    const parts = acl.split('.');
    switch (parts[0]) {
      case 'admin':
        description = `Admin`;
        break;
      case 'crs':
        description = `Care Coordination`;
        break;
      case 'yoCare':
        description = `Yo Care`;
        break;
      case 'engagementHub':
        description = `Engagement Hub`;
        break;
      default:
        description = `All Access`;
        break;
    }
    if (parts.length <= 1) return [description, acl];
    const remainingParts = parts.slice(1);
    const lastPart = remainingParts[remainingParts.length - 1];

    if (actions.includes(lastPart) || lastPart === ALL_WILDCARD) {
      remainingParts.splice(0, 0, lastPart);
      remainingParts.pop();
    }
    description += ` -${remainingParts.reduce((concatenation, part) => {
      if (part === ALL_WILDCARD)
        return `${concatenation} All Access${remainingParts.length > 1 ? ' to' : ''}`;
      return `${concatenation} ${part
        .replace(/(?<!^)([A-Z])/g, ' $1')
        .replace(/([a-z])([A-Z])/g, '$1 $2')}`;
    }, '')}`;
    return [description.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase()), acl];
  });
};

export default crsAcls;
